// Custom hook for marker management with Firestore integration
import { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy,
  where,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../auth/firebase';
import { Marker } from '../types';
import { useAuth } from '../contexts/AuthContext';
// Removed unused uuid import

export const useMarkers = () => {
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  // Subscribe to user's markers
  useEffect(() => {
    if (!currentUser) {
      setMarkers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const markersRef = collection(db, 'markers');
    const q = query(
      markersRef,
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const markersData: Marker[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          markersData.push({
            id: doc.id,
            title: data.title,
            description: data.description,
            latitude: data.latitude,
            longitude: data.longitude,
            userId: data.userId,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            category: data.category || 'other',
            severity: data.severity || 'low'
          });
        });
        setMarkers(markersData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.warn('Firebase permission error - using fallback data:', err.message);
        
        // Provide fallback markers when Firebase is unavailable
        const fallbackMarkers: Marker[] = [
          {
            id: 'fallback-1',
            title: 'Sample Ocean Marker',
            description: 'This is sample data - Firebase unavailable',
            latitude: 37.7749,
            longitude: -122.4194,
            userId: currentUser?.uid || 'demo-user',
            createdAt: new Date(),
            updatedAt: new Date(),
            category: 'hazard',
            severity: 'medium'
          }
        ];
        
        setMarkers(fallbackMarkers);
        setError('Using offline data - Firebase permissions issue');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Add new marker
  const addMarker = async (markerData: Omit<Marker, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!currentUser) {
      throw new Error('User must be logged in to add markers');
    }

    try {
      const now = Timestamp.now();
      const docRef = await addDoc(collection(db, 'markers'), {
        ...markerData,
        userId: currentUser.uid,
        createdAt: now,
        updatedAt: now
      });

      return docRef.id;
    } catch (err) {
      console.error('Error adding marker:', err);
      throw new Error('Failed to add marker');
    }
  };

  // Update existing marker
  const updateMarker = async (markerId: string, updates: Partial<Omit<Marker, 'id' | 'userId' | 'createdAt'>>) => {
    if (!currentUser) {
      throw new Error('User must be logged in to update markers');
    }

    try {
      const markerRef = doc(db, 'markers', markerId);
      await updateDoc(markerRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error updating marker:', err);
      throw new Error('Failed to update marker');
    }
  };

  // Delete marker
  const deleteMarker = async (markerId: string) => {
    if (!currentUser) {
      throw new Error('User must be logged in to delete markers');
    }

    try {
      await deleteDoc(doc(db, 'markers', markerId));
    } catch (err) {
      console.error('Error deleting marker:', err);
      throw new Error('Failed to delete marker');
    }
  };

  // Search markers
  const searchMarkers = (searchTerm: string, category?: string) => {
    return markers.filter(marker => {
      const matchesSearch = !searchTerm || 
        marker.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        marker.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !category || marker.category === category;
      
      return matchesSearch && matchesCategory;
    });
  };

  return {
    markers,
    loading,
    error,
    addMarker,
    updateMarker,
    deleteMarker,
    searchMarkers
  };
};
